import React, { useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';

import { AiChatHistoryService } from '../../api';
import { AiChatMessageInterface } from '../../interfaces/aiChatSettings/aiChatMessages.interface';
import { AiChatHistoryConversationsInterface } from '../../interfaces/aiChatSettings';

function AiChatHistoryComponent({ conversation, onClose }: { conversation: AiChatHistoryConversationsInterface, onClose: () => void }) {
  const [messages, setMessages] = useState<AiChatMessageInterface[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [size] = useState({ width: window.innerWidth - 256 - 32, height: window.innerHeight - 49 - 32 });

  useEffect(() => {
    if (!conversation) return;
    setIsLoading(true);
    AiChatHistoryService.getConversation(conversation.conversationId).subscribe((response) => {
      setMessages(response);
      setIsLoading(false);
    });
  }, [conversation]);

  function convertResponseToHTML(rawText: string) {
    const lines = rawText.split('\n');
    const output = [];
    let inList = false;

    const closeList = () => {
      if (inList) {
        output.push('</ul>');
        inList = false;
      }
    };

    const applyFormatting = (text: string) => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // bold
        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>') // markdown links
        .replace(/\[doc(\d+)\]/g, ''); // remove links
      // .replace(/\[doc(\d+)\]/g, '<a href="document$1.pdf" target="_blank">[doc$1]</a>'); // links
    };

    for (let line of lines) {
      line = line.trim();

      // Skip empty lines
      if (line === '') {
        closeList();
        continue;
      }

      // Header (### ...)
      if (line.startsWith('###')) {
        closeList();
        const content = line.replace(/^###\s*/, '').trim();
        output.push(`<h3>${applyFormatting(content)}</h3>`);
        continue;
      }

      // List item (- ...)
      if (line.startsWith('- ')) {
        if (!inList) {
          output.push('<ul>');
          inList = true;
        }
        const item = line.replace(/^- /, '').trim();
        output.push(`<li>${applyFormatting(item)}</li>`);
        continue;
      }

      // Paragraph
      closeList();
      output.push(`<p>${applyFormatting(line)}</p>`);
    }

    // Close list if still open
    closeList();

    return output.join('\n');
  }

  // ================================ RENDER FUNCTIONS ================================

  function noMessagesRender() {
    return (
      <div className="chat-window__body-messages-empty">
        <p>Nema poruka</p>
      </div>
    );
  }

  function messageRender(message: AiChatMessageInterface, key: number) {
    return (
      <div
        id={`ai-chat-message-${key}`}
        key={key}
        className={`chat-window__body-messages-bubble ${
          message.role !== 'user'
            ? 'chat-window__body-messages-bubble--right'
            : 'chat-window__body-messages-bubble--left'
        }`}
      >
        <div dangerouslySetInnerHTML={{ __html: convertResponseToHTML(message.message) }} />
      </div>
    );
  }

  function headerNameRender() {
    const [timestamp] = conversation.conversationId.split('-');
    const date = new Date(parseInt(timestamp)).toLocaleDateString('hr-HR');
    const time = new Date(parseInt(timestamp)).toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
    return (
      <div className="chat-window__header-name">
        <h1>
          {conversation.user.name} - {date} {time}
        </h1>
      </div>
    );
  }

  function headerActionRender() {
    return (
      <div className="chat-window__header-actions">
        <CloseOutlined onClick={onClose} />
      </div>
    );
  }

  function renderChatBody() {
    return (
      <div className="chat-window__body">
        <div className="chat-window__body-messages">
          {messages.length === 0 ? noMessagesRender() : messages.map((message, key) => messageRender(message, key))}
        </div>
      </div>
    );
  }

  function chatWindowRender() {
    return (
      <div className="chat-window" style={{ width: size.width, height: size.height }}>
        <div className="chat-window__header">
          {headerNameRender()}
          {headerActionRender()}
        </div>
        {renderChatBody()}
      </div>
    );
  }

  return <div className="ai-chat ai-chat--open" style={{ zIndex: 999999, overflow: 'hidden' }}>{chatWindowRender()}</div>;
}

export default AiChatHistoryComponent;
